import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useLocationStateStack from "../../app/useLocationStateStack";
import { trackProductEvent } from "../../analytics/trackEvent";
import { mergeUserPreservingNewerUsername } from "../../auth/userFreshness";

const RANK_LADDER = [
  "Newcomer",
  "Newcomer Bronze",
  "Newcomer Silver",
  "Newcomer Gold",
  "Proven Angler",
  "Proven Angler Bronze",
  "Proven Angler Silver",
  "Proven Angler Gold",
  "Expert Angler",
  "Expert Angler Bronze",
  "Expert Angler Silver",
  "Expert Angler Gold",
  "Expert Angler Platinum",
  "Pro Angler",
  "Pro Angler Bronze",
  "Pro Angler Silver",
  "Pro Angler Gold",
  "Pro Angler Platinum",
  "Legendary Angler",
  "Legendary Angler Bronze",
  "Legendary Angler Silver",
  "Legendary Angler Gold",
  "Legendary Angler Platinum",
  "Legendary Angler Diamond",
];

const XP_TO_NEXT_LEVEL = [
  110,
  150,
  200,
  260,
  330,
  410,
  500,
  600,
  720,
  860,
  1020,
  1200,
  1400,
  1620,
  1860,
  2120,
  2400,
  2700,
  3020,
  3360,
  3720,
  4100,
  4500,
];

const BADGE_BASE_PATH = "/badges/";
const BADGE_EXTENSION = ".png";

const CUMULATIVE_XP_TARGETS = XP_TO_NEXT_LEVEL.reduce((totals, xpNeeded) => {
  const previousTotal = totals.length ? totals[totals.length - 1] : 0;
  totals.push(previousTotal + xpNeeded);
  return totals;
}, []);

function normalizeTimestampToMillis(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed < 1e12 ? parsed * 1000 : parsed;
}

function compareUsersByRanking(leftUser, rightUser) {
  const xpDifference =
    asNonNegativeNumber(rightUser?.progression?.xp) -
    asNonNegativeNumber(leftUser?.progression?.xp);

  if (xpDifference !== 0) {
    return xpDifference;
  }

  const leftCreatedAt = normalizeTimestampToMillis(leftUser?.createdAt);
  const rightCreatedAt = normalizeTimestampToMillis(rightUser?.createdAt);

  if (leftCreatedAt !== rightCreatedAt) {
    if (leftCreatedAt === null) return 1;
    if (rightCreatedAt === null) return -1;
    return leftCreatedAt - rightCreatedAt;
  }

  return String(leftUser?.username || "").localeCompare(
    String(rightUser?.username || ""),
    undefined,
    { sensitivity: "base" }
  );
}

function asNonNegativeNumber(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
}

function asPositiveNumberOrNull(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

function asZeroOrPositiveNumberOrNull(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

function getLevelStartXp(level) {
  const numericLevel = asNonNegativeNumber(level, 1);
  if (numericLevel <= 1) return 0;
  return CUMULATIVE_XP_TARGETS[numericLevel - 2] || 0;
}

export function getRankNameForLevel(level) {
  const numericLevel = Math.max(
    1,
    Math.min(Math.floor(asNonNegativeNumber(level, 1)), RANK_LADDER.length)
  );
  return RANK_LADDER[numericLevel - 1] || RANK_LADDER[0];
}

export function getBadgeKeyForRankName(rankName) {
  const badgeKeyChars = [];

  for (const char of normalizeDetailText(rankName).toLowerCase()) {
    if (/[a-z0-9]/.test(char)) {
      badgeKeyChars.push(char);
      continue;
    }

    if ([" ", "-", "_"].includes(char)) {
      if (!badgeKeyChars.length || badgeKeyChars[badgeKeyChars.length - 1] === "-") {
        continue;
      }
      badgeKeyChars.push("-");
    }
  }

  if (badgeKeyChars[badgeKeyChars.length - 1] === "-") {
    badgeKeyChars.pop();
  }

  return badgeKeyChars.join("") || "newcomer";
}

function formatNumber(value) {
  return new Intl.NumberFormat().format(asNonNegativeNumber(value));
}

function normalizeDetailText(value) {
  if (typeof value === "string") {
    return value.trim();
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
}

function humanizeDetailValue(value) {
  return normalizeDetailText(value)
    .split(/[_-]/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function humanizeOptionValue(value) {
  const normalized = normalizeDetailText(value);
  if (!normalized) return "";

  const optionValue = normalized.includes(".")
    ? normalized.split(".").pop() || normalized
    : normalized;

  return humanizeDetailValue(optionValue);
}

function translateDetailValue(t, value, overrideKey = null, overrideDefaultValue = null) {
  const normalized = normalizeDetailText(value);
  if (!normalized) return "";

  const translationKey = overrideKey || normalized;
  const defaultValue = overrideDefaultValue || humanizeDetailValue(normalized);

  return t(translationKey, { defaultValue });
}

function formatTranslatedDetailList(t, values) {
  if (!Array.isArray(values)) return "";

  const normalizedItems = values
    .map((value) => normalizeDetailText(value))
    .filter(Boolean);

  if (!normalizedItems.length) return "";

  return normalizedItems
    .map((value) =>
      t(value, {
        defaultValue: humanizeOptionValue(value),
      })
    )
    .join(", ");
}

function buildPreferenceChips(t, profileDetails) {
  if (!profileDetails || typeof profileDetails !== "object") return [];

  const chips = [];
  const waterType = normalizeDetailText(profileDetails.waterType).toLowerCase();
  const fishingStyle = normalizeDetailText(profileDetails.fishingStyle).toLowerCase();
  const methods = Array.isArray(profileDetails.methods)
    ? profileDetails.methods
    : [];

  if (waterType === "freshwater") {
    chips.push({
      key: "waterType:freshwater",
      label: t("freshwater", { defaultValue: "Freshwater" }),
    });
  } else if (waterType === "saltwater") {
    chips.push({
      key: "waterType:saltwater",
      label: t("saltwater", { defaultValue: "Saltwater / Marine" }),
    });
  }

  if (fishingStyle === "shore") {
    chips.push({
      key: "fishingStyle:shore",
      label: t("shore", { defaultValue: "Shore" }),
    });
  } else if (fishingStyle === "boat") {
    chips.push({
      key: "fishingStyle:boat",
      label: t("boat", { defaultValue: "Boat" }),
    });
  } else if (fishingStyle === "both") {
    chips.push({
      key: "fishingStyle:both",
      label: t("profile_way_shore_boat", { defaultValue: "Shore + Boat" }),
    });
  }

  methods
    .map((method) => normalizeDetailText(method))
    .filter(Boolean)
    .forEach((method) => {
      chips.push({
        key: `method:${method}`,
        label: t(method, { defaultValue: humanizeOptionValue(method) }),
      });
    });

  return chips;
}

function normalizeExternalUrl(value) {
  const normalized = normalizeDetailText(value);
  if (!normalized) return "";

  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }

  return `https://${normalized}`;
}

function renderExternalUrl(value) {
  const displayValue = normalizeDetailText(value);
  if (!displayValue) return "";

  return (
    <a
      href={normalizeExternalUrl(displayValue)}
      target="_blank"
      rel="noreferrer"
      className="profile-progression-modal-link"
    >
      {displayValue}
    </a>
  );
}

function formatMonthYear(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const directNumber =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim()
      ? Number(value)
      : Number.NaN;

  let parsedDate = null;

  if (Number.isFinite(directNumber) && directNumber > 0) {
    const timestamp = directNumber < 1e12 ? directNumber * 1000 : directNumber;
    parsedDate = new Date(timestamp);
  } else if (typeof value === "string") {
    parsedDate = new Date(value);
  } else if (value instanceof Date) {
    parsedDate = value;
  }

  if (!(parsedDate instanceof Date) || Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    year: "numeric",
  }).format(parsedDate);
}

function buildBadgeCandidates(badgeKey) {
  if (!badgeKey) return [];

  const publicUrl = process.env.PUBLIC_URL || "";
  const normalizedKey = String(badgeKey).trim().toLowerCase();

  return [`${publicUrl}${BADGE_BASE_PATH}${normalizedKey}${BADGE_EXTENSION}`];
}

function resolveMilestoneReward(progression) {
  const directMilestone =
    asZeroOrPositiveNumberOrNull(progression?.milestonePosition) ??
    asZeroOrPositiveNumberOrNull(progression?.signupPosition) ??
    asZeroOrPositiveNumberOrNull(progression?.milestone);

  if (directMilestone !== null) {
    return {
      type: "signup-position",
      milestone: directMilestone,
    };
  }

  const rewards = progression?.milestoneRewards;
  if (!Array.isArray(rewards)) return null;

  const signupReward = rewards.find(
    (reward) =>
      reward &&
      reward.type === "signup-position" &&
      asZeroOrPositiveNumberOrNull(reward.milestone) !== null
  );

  if (!signupReward) return null;

  return {
    ...signupReward,
    milestone: asZeroOrPositiveNumberOrNull(signupReward.milestone),
  };
}

function getMilestonePopupCopy(t, milestoneReward) {
  const milestone = asZeroOrPositiveNumberOrNull(milestoneReward?.milestone);
  if (milestone === null) return null;

  if (milestone === 0) {
    return {
      title: t("progression_milestone_popup_title_0", {
        defaultValue: "Founder Account",
      }),
      body: t("progression_milestone_popup_body_0", {
        defaultValue:
          "This account belongs to the founder of the app and was created before public user registration began.",
      }),
    };
  }

  if (milestone === 1) {
    return {
      title: t("progression_milestone_popup_title_1", {
        defaultValue: "First Public User",
      }),
      body: t("progression_milestone_popup_body_1", {
        defaultValue:
          "You were the first registered user after the founder account, making you the first public user of the app.",
      }),
    };
  }

  if (milestone === 100) {
    return {
      title: t("progression_milestone_popup_title_100", {
        defaultValue: "Early Supporter",
      }),
      body: t("progression_milestone_popup_body_100", {
        defaultValue:
          "You were among the first 100 registered users of the app.",
      }),
    };
  }

  if (milestone === 1000) {
    return {
      title: t("progression_milestone_popup_title_1000", {
        defaultValue: "Growth Milestone",
      }),
      body: t("progression_milestone_popup_body_1000", {
        defaultValue:
          "You were among the first 1,000 registered users of the app, marking an early milestone in its growth.",
      }),
    };
  }

  if (milestone === 10000) {
    return {
      title: t("progression_milestone_popup_title_10000", {
        defaultValue: "Milestone User",
      }),
      body: t("progression_milestone_popup_body_10000", {
        defaultValue:
          "You were among the first 10,000 registered users of the app, part of a major milestone in the app's growth.",
      }),
    };
  }

  return {
    title: t("progression_milestone_popup_title_default", {
      defaultValue: "Milestone User",
    }),
    body: t("progression_milestone_popup_body_default", {
      defaultValue:
        "You reached a milestone position in the app's growth as one of its early registered users.",
    }),
  };
}

export default function useProfileProgressionView({
  progression,
  profileDetails = null,
  directoryUsers = null,
  isOwnProfile = false,
  bioEditRequest = null,
  isBioEditRequestReady = false,
  onBioEditRequestConsumed,
}) {
  const { t } = useTranslation();
  const [badgeSrc, setBadgeSrc] = useState(null);
  const [nextBadgeSrc, setNextBadgeSrc] = useState(null);
  const [globalRankState, setGlobalRankState] = useState({
    status: "idle",
    position: null,
  });
  const [activeInfoModal, setActiveInfoModal] = useState(null);
  const [currentProfileDetails, setCurrentProfileDetails] = useState(
    profileDetails && typeof profileDetails === "object" ? profileDetails : null
  );
  const [isBioEditorOpen, setIsBioEditorOpen] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [doesBioOverflow, setDoesBioOverflow] = useState(false);
  const bioTextRef = useRef(null);
  const consumedBioEditRequestRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setBadgeSrc(null);

    const badgeKey = progression?.currentBadgeKey;
    const candidates = buildBadgeCandidates(badgeKey);
    if (!candidates.length) return undefined;

    const tryCandidate = (index) => {
      if (cancelled || index >= candidates.length) return;

      const image = new Image();
      image.onload = () => {
        if (!cancelled) {
          setBadgeSrc(candidates[index]);
        }
      };
      image.onerror = () => {
        tryCandidate(index + 1);
      };
      image.src = candidates[index];
    };

    tryCandidate(0);

    return () => {
      cancelled = true;
    };
  }, [progression?.currentBadgeKey]);

  const currentLevel = Math.max(
    1,
    Math.floor(asNonNegativeNumber(progression?.currentLevel, 1))
  );
  const currentRankName =
    progression?.currentRankName ||
    t("progression_rank_default", { defaultValue: "Newcomer" });
  const xp = asNonNegativeNumber(progression?.xp);
  const credits = asNonNegativeNumber(progression?.credits);
  const nextLevelXpTarget = asPositiveNumberOrNull(progression?.nextLevelXpTarget);
  const nextLevel = nextLevelXpTarget
    ? Math.min(currentLevel + 1, RANK_LADDER.length)
    : null;
  const nextRankName = nextLevel ? getRankNameForLevel(nextLevel) : "";
  const nextBadgeKey = nextRankName ? getBadgeKeyForRankName(nextRankName) : "";
  const globalRankUserId = normalizeDetailText(profileDetails?.id);
  const globalRankUserEmail = normalizeDetailText(profileDetails?.email).toLowerCase();
  const globalRankUsername = normalizeDetailText(profileDetails?.username).toLowerCase();

  useEffect(() => {
    let cancelled = false;
    setNextBadgeSrc(null);

    const candidates = buildBadgeCandidates(nextBadgeKey);
    if (!candidates.length) return undefined;

    const tryCandidate = (index) => {
      if (cancelled || index >= candidates.length) return;

      const image = new Image();
      image.onload = () => {
        if (!cancelled) {
          setNextBadgeSrc(candidates[index]);
        }
      };
      image.onerror = () => {
        tryCandidate(index + 1);
      };
      image.src = candidates[index];
    };

    tryCandidate(0);

    return () => {
      cancelled = true;
    };
  }, [nextBadgeKey]);

  useEffect(() => {
    if (
      !isOwnProfile ||
      (!globalRankUserId && !globalRankUserEmail && !globalRankUsername)
    ) {
      setGlobalRankState({ status: "idle", position: null });
      return undefined;
    }

    if (!Array.isArray(directoryUsers) || directoryUsers.length === 0) {
      setGlobalRankState({ status: "loading", position: null });
      return undefined;
    }

    const rankedUsers = [...directoryUsers].sort(compareUsersByRanking);
    const rankIndex = rankedUsers.findIndex((rankedUser) => {
      const rankedId = normalizeDetailText(rankedUser?.id);
      if (globalRankUserId && rankedId && rankedId === globalRankUserId) {
        return true;
      }

      const rankedEmail = normalizeDetailText(rankedUser?.email).toLowerCase();
      if (
        globalRankUserEmail &&
        rankedEmail &&
        rankedEmail === globalRankUserEmail
      ) {
        return true;
      }

      const rankedUsername = normalizeDetailText(rankedUser?.username).toLowerCase();
      return Boolean(
        globalRankUsername &&
          rankedUsername &&
          rankedUsername === globalRankUsername
      );
    });

    setGlobalRankState({
      status: "ready",
      position: rankIndex >= 0 ? rankIndex + 1 : null,
    });

    return undefined;
  }, [
    directoryUsers,
    globalRankUserEmail,
    globalRankUserId,
    globalRankUsername,
    isOwnProfile,
  ]);

  useEffect(() => {
    if (!activeInfoModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveInfoModal(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeInfoModal]);

  useEffect(() => {
    setCurrentProfileDetails((currentDetails) =>
      profileDetails && typeof profileDetails === "object"
        ? mergeUserPreservingNewerUsername(currentDetails, profileDetails)
        : null
    );
  }, [profileDetails]);

  useEffect(() => {
    if (activeInfoModal !== "bio") {
      setIsBioExpanded(false);
      setDoesBioOverflow(false);
      return undefined;
    }

    const measureBioOverflow = () => {
      const bioTextElement = bioTextRef.current;
      if (!bioTextElement) {
        setDoesBioOverflow(false);
        return;
      }

      const computedStyle = window.getComputedStyle(bioTextElement);
      const lineHeight = Number.parseFloat(computedStyle.lineHeight);
      if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
        setDoesBioOverflow(bioTextElement.scrollHeight > bioTextElement.clientHeight + 1);
        return;
      }

      setDoesBioOverflow(bioTextElement.scrollHeight > lineHeight * 3 + 2);
    };

    measureBioOverflow();
    window.addEventListener("resize", measureBioOverflow);

    return () => {
      window.removeEventListener("resize", measureBioOverflow);
    };
  }, [activeInfoModal, currentProfileDetails?.bio]);

  const levelStartXp = nextLevelXpTarget ? getLevelStartXp(currentLevel) : xp;
  const levelSpanXp = nextLevelXpTarget
    ? Math.max(1, nextLevelXpTarget - levelStartXp)
    : 1;
  const progressXp = nextLevelXpTarget
    ? Math.max(0, Math.min(xp - levelStartXp, levelSpanXp))
    : levelSpanXp;
  const progressPercent = nextLevelXpTarget
    ? Math.max(0, Math.min(100, (progressXp / levelSpanXp) * 100))
    : 100;
  const remainingXp = nextLevelXpTarget ? Math.max(0, nextLevelXpTarget - xp) : 0;
  const milestoneReward = resolveMilestoneReward(progression);
  const milestoneValue =
    milestoneReward &&
    milestoneReward.milestone !== null &&
    milestoneReward.milestone !== undefined
      ? milestoneReward.milestone
      : null;
  const milestoneLabel =
    milestoneValue !== null
      ? t("progression_milestone_label", {
          defaultValue: "Milestone #{{milestone}}",
          milestone: formatNumber(milestoneValue),
        })
      : "";
  const milestonePopupCopy = getMilestonePopupCopy(t, milestoneReward);
  const showPrivateDetails = Boolean(isOwnProfile);
  const normalizedProfileDetails =
    currentProfileDetails && typeof currentProfileDetails === "object"
      ? currentProfileDetails
      : null;
  const showBioDetails = Boolean(normalizedProfileDetails);
  const preferenceChips = buildPreferenceChips(t, normalizedProfileDetails);
  const mobileBioText = normalizeDetailText(normalizedProfileDetails?.bio);
  const formattedGlobalRank =
    globalRankState.status === "loading"
      ? t("loading", { defaultValue: "Loading..." })
      : globalRankState.position
      ? t("profile_global_rank_value", {
          defaultValue: "No.{{rank}}",
          rank: formatNumber(globalRankState.position),
        })
      : "\u2014";
  const joinedMonthYear = formatMonthYear(normalizedProfileDetails?.createdAt);
  const normalizedSocial =
    normalizedProfileDetails?.social &&
    typeof normalizedProfileDetails.social === "object"
      ? normalizedProfileDetails.social
      : {};
  const bioTextRow = normalizedProfileDetails
    ? {
        label: t("profile_bio_text_label", {
          defaultValue: "Bio",
        }),
        value: normalizeDetailText(normalizedProfileDetails.bio),
      }
    : null;
  const bioDetailsRows = normalizedProfileDetails
    ? [
        {
          label: t("profile_bio_country_label", {
            defaultValue: "Country",
          }),
          value: normalizeDetailText(normalizedProfileDetails.country),
        },
        {
          label: t("profile_bio_city_label", {
            defaultValue: "City",
          }),
          value: normalizeDetailText(normalizedProfileDetails.city),
        },
        {
          label: t("profile_bio_account_type_label", {
            defaultValue: "Account type",
          }),
          value: translateDetailValue(
            t,
            normalizedProfileDetails.accountType,
            normalizeDetailText(normalizedProfileDetails.accountType),
            humanizeDetailValue(normalizedProfileDetails.accountType)
          ),
        },
        {
          label: t("profile_bio_experience_label", {
            defaultValue: "Experience",
          }),
          value: translateDetailValue(
            t,
            normalizedProfileDetails.experience,
            normalizeDetailText(normalizedProfileDetails.experience),
            humanizeDetailValue(normalizedProfileDetails.experience)
          ),
        },
        {
          label: t("profile_bio_fishing_style_label", {
            defaultValue: "Way of Fishing",
          }),
          value: translateDetailValue(
            t,
            normalizedProfileDetails.fishingStyle,
            normalizeDetailText(normalizedProfileDetails.fishingStyle),
            humanizeDetailValue(normalizedProfileDetails.fishingStyle)
          ),
        },
        {
          label: t("profile_bio_water_type_label", {
            defaultValue: "Water type",
          }),
          value: translateDetailValue(
            t,
            normalizedProfileDetails.waterType,
            normalizeDetailText(normalizedProfileDetails.waterType),
            humanizeDetailValue(normalizedProfileDetails.waterType)
          ),
        },
        {
          label: t("profile_bio_range_type_label", {
            defaultValue: "Fishing range",
          }),
          value: translateDetailValue(
            t,
            normalizedProfileDetails.rangeType,
            normalizeDetailText(normalizedProfileDetails.rangeType),
            humanizeDetailValue(normalizedProfileDetails.rangeType)
          ),
        },
        {
          label: t("profile_bio_methods_label", {
            defaultValue: "Type of Fishing",
          }),
          value: formatTranslatedDetailList(t, normalizedProfileDetails.methods),
        },
        {
          label: t("profile_bio_species_label", {
            defaultValue: "Favorite species",
          }),
          value: formatTranslatedDetailList(
            t,
            normalizedProfileDetails.favoriteSpecies
          ),
        },
        {
          label: t("profile_bio_instagram_label", {
            defaultValue: "Instagram URL",
          }),
          value: renderExternalUrl(normalizedSocial.instagram),
        },
        {
          label: t("profile_bio_youtube_label", {
            defaultValue: "YouTube URL",
          }),
          value: renderExternalUrl(normalizedSocial.youtube),
        },
        {
          label: t("profile_bio_facebook_label", {
            defaultValue: "Facebook URL",
          }),
          value: renderExternalUrl(normalizedSocial.facebook),
        },
        {
          label: t("profile_bio_website_label", {
            defaultValue: "Website",
          }),
          value: renderExternalUrl(normalizedSocial.website),
        },
      ].filter((row) => row.value)
    : [];
  const badgePopupCopy = showPrivateDetails
    ? {
        dialogLabel: t("progression_badge_popup_dialog_label", {
          defaultValue: "Rank badge information",
        }),
        kicker: t("progression_badge_popup_kicker", {
          defaultValue: "Profile progression",
        }),
        title: t("progression_badge_popup_title", {
          defaultValue: "Rank Badge",
        }),
        body: t("progression_badge_popup_body", {
          defaultValue:
            "Your badge reflects your current rank in the app. As you earn XP and reach new levels, your rank and badge progress to the next tier.",
        }),
        badgeImage: badgeSrc,
        badgeAlt: t("progression_current_badge", {
          defaultValue: "Current badge",
        }),
        pill: t("progression_badge_popup_pill", {
          defaultValue: "Level {{level}} - {{rank}}",
          level: currentLevel,
          rank: currentRankName,
        }),
      }
    : null;
  const nextBadgePopupCopy =
    showPrivateDetails && nextRankName
      ? {
          dialogLabel: t("progression_next_badge_popup_dialog_label", {
            defaultValue: "Next rank badge information",
          }),
          kicker: t("progression_next_badge_popup_kicker", {
            defaultValue: "Next badge",
          }),
          title: nextRankName,
          body: t("progression_next_badge_popup_body", {
            defaultValue:
              "This is the next badge in your progression. Keep earning XP to reach the next level and unlock this rank.",
          }),
          badgeImage: nextBadgeSrc,
          badgeAlt: t("progression_next_badge", {
            defaultValue: "Next badge",
          }),
          pill: t("progression_next_badge_popup_pill", {
            defaultValue: "{{xp}} XP remaining",
            xp: formatNumber(remainingXp),
          }),
        }
      : null;
  const xpPopupCopy = showPrivateDetails
    ? {
        dialogLabel: t("progression_xp_popup_dialog_label"),
        kicker: t("progression_xp_popup_kicker"),
        title: t("progression_xp_popup_title"),
        body: t("progression_xp_popup_body"),
        bullets: [
          t("progression_xp_popup_detail_catches_trips"),
          t("progression_xp_popup_detail_catch_details"),
          t("progression_xp_popup_detail_engagement"),
          t("progression_xp_popup_detail_checklists"),
        ],
        pill: t("progression_xp_popup_pill", {
          xp: formatNumber(xp),
        }),
      }
    : null;
  const levelPopupCopy = showPrivateDetails
    ? {
        dialogLabel: t("progression_level_popup_dialog_label"),
        kicker: t("progression_level_popup_kicker"),
        title: t("progression_level_popup_title"),
        body: t("progression_level_popup_body"),
        bullets: [
          t("progression_level_popup_detail_xp"),
          t("progression_level_popup_detail_activity"),
          t("progression_level_popup_detail_long_term"),
        ],
        pill: t("progression_level_popup_pill", {
          level: currentLevel,
        }),
      }
    : null;
  const rankPopupCopy = showPrivateDetails
    ? {
        dialogLabel: t("progression_rank_popup_dialog_label"),
        kicker: t("progression_rank_popup_kicker"),
        title: t("progression_rank_popup_title"),
        body: t("progression_rank_popup_body"),
        bullets: [
          t("progression_rank_popup_detail_unlock"),
          t("progression_rank_popup_detail_activity"),
          t("progression_rank_popup_detail_profile"),
        ],
        pill: currentRankName,
      }
    : null;
  const bioPopupCopy = showBioDetails
    ? {
        dialogLabel: t("profile_bio_modal_dialog_label", {
          defaultValue: "Profile details",
        }),
        kicker: t("profile_bio_modal_kicker", {
          defaultValue: "Profile Bio and Interests",
        }),
        title: null,
        bioRow: bioTextRow?.value ? bioTextRow : null,
        rows: bioDetailsRows,
        emptyMessage: t("profile_bio_modal_empty", {
          defaultValue: "No profile details available.",
        }),
      }
    : null;
  const activeModalCopy =
    activeInfoModal === "bio"
      ? bioPopupCopy
      : activeInfoModal === "milestone"
      ? milestonePopupCopy &&
        milestoneLabel && {
          dialogLabel: t("progression_milestone_popup_dialog_label", {
            defaultValue: "Milestone information",
          }),
          kicker: t("progression_milestone_popup_kicker", {
            defaultValue: "Signup milestone",
          }),
          title: milestonePopupCopy.title,
          body: milestonePopupCopy.body,
          pill: milestoneLabel,
        }
      : activeInfoModal === "badge"
      ? badgePopupCopy
      : activeInfoModal === "next-badge"
      ? nextBadgePopupCopy
      : activeInfoModal === "xp"
      ? xpPopupCopy
      : activeInfoModal === "level"
      ? levelPopupCopy
      : activeInfoModal === "rank"
      ? rankPopupCopy
      : null;

  const openInfoModal = useCallback((modalKey) => setActiveInfoModal(modalKey), []);
  const closeInfoModal = useCallback(() => setActiveInfoModal(null), []);
  const handleOpenBioEditor = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    trackProductEvent("edit_bio_opened", {
      featureArea: "profile",
      metadata: { source: "profile_progression" },
    });
    setActiveInfoModal(null);
    setIsBioEditorOpen(true);
  }, []);
  useEffect(() => {
    const requestId = bioEditRequest?.id;
    if (
      !isOwnProfile ||
      !isBioEditRequestReady ||
      !requestId ||
      !currentProfileDetails ||
      consumedBioEditRequestRef.current === requestId
    ) {
      return;
    }

    consumedBioEditRequestRef.current = requestId;
    handleOpenBioEditor();
    onBioEditRequestConsumed?.(requestId);
  }, [
    bioEditRequest,
    currentProfileDetails,
    handleOpenBioEditor,
    isBioEditRequestReady,
    isOwnProfile,
    onBioEditRequestConsumed,
  ]);
  const handleCloseBioEditor = useCallback(() => {
    setIsBioEditorOpen(false);
    setActiveInfoModal("bio");
  }, []);
  const handleBioDetailsSaved = useCallback((updatedProfileDetails) => {
    if (updatedProfileDetails && typeof updatedProfileDetails === "object") {
      setCurrentProfileDetails((currentDetails) =>
        mergeUserPreservingNewerUsername(currentDetails, updatedProfileDetails)
      );
    }

    setIsBioEditorOpen(false);
    setActiveInfoModal("bio");
  }, []);
  const handleUsernameSaved = useCallback((updatedProfileDetails) => {
    if (updatedProfileDetails && typeof updatedProfileDetails === "object") {
      setCurrentProfileDetails((currentDetails) =>
        mergeUserPreservingNewerUsername(currentDetails, updatedProfileDetails)
      );
    }
  }, []);
  const toggleBioExpanded = useCallback(() => {
    setIsBioExpanded((previousValue) => !previousValue);
  }, []);

  const progressionHistoryItems = useMemo(
    () => [
      {
        id: "profile-progression-info",
        isOpen: Boolean(activeInfoModal),
        close: closeInfoModal,
      },
      {
        id: "profile-progression-bio-editor",
        isOpen: isBioEditorOpen,
        close: handleCloseBioEditor,
      },
    ],
    [
      activeInfoModal,
      closeInfoModal,
      handleCloseBioEditor,
      isBioEditorOpen,
    ]
  );

  useLocationStateStack("profileProgressionStack", progressionHistoryItems);

  return {
    badgeSrc,
    activeInfoModal,
    activeModalCopy,
    showPrivateDetails,
    showBioDetails,
    currentRankName,
    currentLevel,
    nextRankName,
    joinedMonthYear,
    milestoneLabel,
    formattedXp: formatNumber(xp),
    formattedCredits: formatNumber(credits),
    formattedGlobalRank,
    formattedNextLevelXpTarget: nextLevelXpTarget
      ? `${formatNumber(nextLevelXpTarget)} XP`
      : null,
    formattedLevelStartXp: formatNumber(levelStartXp),
    formattedCurrentXp: formatNumber(xp),
    formattedGoalXp: nextLevelXpTarget ? formatNumber(nextLevelXpTarget) : null,
    formattedProgress: nextLevelXpTarget
      ? `${formatNumber(progressXp)} / ${formatNumber(levelSpanXp)} XP`
      : null,
    formattedRemainingXp: formatNumber(remainingXp),
    nextLevelXpTarget,
    progressPercent,
    nextBadgeSrc,
    preferenceChips,
    mobileBioText,
    normalizedProfileDetails,
    bioTextRef,
    isBioExpanded,
    doesBioOverflow,
    isBioEditorOpen,
    openInfoModal,
    closeInfoModal,
    handleOpenBioEditor,
    handleCloseBioEditor,
    handleBioDetailsSaved,
    handleUsernameSaved,
    toggleBioExpanded,
  };
}
